import { useTranslation } from '@thoughtspot/i18n';
import { UserAction, userActionTypeEnum } from '@thoughtspot/metrics';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { Link } from '@thoughtspot/radiant-react/widgets/link';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { Colors, Typography, } from '@thoughtspot/radiant-react/widgets/typography';
import { isOktaEnabled, isSamlEnabled, } from '@thoughtspot/system-config-service';
import cx from 'classnames';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState, } from 'react';
import { setSaml, useDeleteSamlIamV1Mutation, useGetSamlIamV1Query, } from "../../../services/system/system-service";
import { BasePageContext } from "../../../contexts/basepage-context";
import { onDownloadCertificateUtil } from "../../../services/keystore/keystore-service";
import { GenericError } from '../../generic-error/generic-error.container';
import { IamV2MigrationAutomationBanner } from '../../saml/iamv2-migration/iamv2-migration.container';
import { Content, CreateModal, Header, WithQueryHandler, } from '../../system/common';
import { ActionButtons, LearnMore, Mode, TranslationsKeys as TranslationsKeysCommon, } from '../../system/common/constants';
import { getConfig } from '../../system/common/form-builder-config';
import { getFormFields } from '../../system/common/utils';
import { SAML_DEFAULT_RESPONSE_SKEW, SAML_FORM_FIELDS, TranslationsKeys, } from '../constants';
import styles from './saml.module.scss';
const CALLOSUM_CERTIFICATE_PATH = '/callosum/v1/keystore/certificate/current';
const METADATA_PATH = '/managementconsole/admin-api/saml/downloadmetadata';
const METADATA_FILE_NAME = 'metadata.xml';
const pageName = 'saml';
const initialData = {
    server_name: '',
    protocol: '',
    server_port: '',
    service_name: '',
    response_skew: 3600,
    idp_metadata: '',
    enable_saml: false,
    automatically_create_users: false,
};
const SamlContainer = ({ onSuccess, onError, showLoader, hideLoader, }) => {
    const { t } = useTranslation();
    const [data, setData] = useState({});
    const [mode, setMode] = useState(Mode.VIEW);
    const [formFields, setFormFields] = useState([]);
    const [deleteConfirm, setDeleteConfirm] = React.useState(false);
    const [screenLoader, setScreenLoader] = useState(false);
    const [shouldDisplayLoaderText, setShouldDisplayLoaderText] = useState(false);
    const isMigrationBannerVisible = isOktaEnabled() && isSamlEnabled();
    const getTranslations = (key, args) => t(`${key}`, args);
    const { metricsService } = BasePageContext();
    const formConfig = useMemo(() => getConfig(pageName), []);
    const onSaveSuccess = (response) => {
        setMode(Mode.VIEW);
        hideLoader();
        if (response?.Error) {
            if (response.Error.includes('Invalid service name. Avoid using')) {
                onError(response.Error);
            }
            else {
                onError(getTranslations('admin.error.generic'));
            }
        }
        else {
            const newData = response.Data || {};
            setData(newData?.enable_saml
                ? {
                    ...newData,
                    ...(newData?.idp_metadata
                        ? { idp_metadata: METADATA_FILE_NAME }
                        : {}),
                }
                : initialData);
            if (newData?.enable_saml) {
                metricsService.collectEvent(new UserAction(userActionTypeEnum.CONFIGURE_SAML_IAM_V1), {
                    autoAddSAMLUsers: newData?.automatically_create_users,
                });
                onSuccess(getTranslations(TranslationsKeys.ADD_UPDATE_SUCCESS));
            }
        }
    };
    const onSaveError = (error) => {
        hideLoader();
        onError(getTranslations('admin.error.generic'));
    };
    const { data: samlData, loading: isLoading, error, } = useGetSamlIamV1Query();
    const fetchSaml = () => {
        if (samlData && !isLoading && samlData.Data) {
            const newData = samlData.Data || {};
            setData(newData?.enable_saml
                ? {
                    ...newData,
                    ...(newData?.idp_metadata
                        ? { idp_metadata: METADATA_FILE_NAME }
                        : {}),
                    response_skew: newData?.response_skew || 3600,
                }
                : initialData);
        }
        else if (samlData && !isLoading && samlData.Error) {
            onError(getTranslations(TranslationsKeys.MESSAGE_API_FAILURE));
        }
        hideLoader();
    };
    const postSamlData = async (samlPostFormData) => {
        try {
            showLoader();
            const postResponse = await setSaml(samlPostFormData);
            if (postResponse.Data) {
                onSaveSuccess(postResponse);
            }
            else {
                onSaveError(postResponse.Error);
            }
            return postResponse;
        }
        catch (postError) {
            onSaveError(postError);
            return postError;
        }
    };
    const [deleteSaml] = useDeleteSamlIamV1Mutation(response => {
        setMode(Mode.VIEW);
        hideLoader();
        const newData = response.Data || {};
        setData(newData?.enable_saml ? newData : initialData);
        if (newData?.enable_saml) {
            onSuccess(getTranslations(TranslationsKeys.DELETE_SUCCESS));
            metricsService.collectEvent(new UserAction(userActionTypeEnum.REMOVE_SAML_IAM_V1), {});
        }
    }, onError);
    const getFieldConfig = (field) => {
        switch (field.key) {
            case 'automatically_create_users':
                return {
                    children: [
                        {
                            label: getTranslations(TranslationsKeysCommon.YES),
                            value: 'true',
                        },
                        {
                            label: getTranslations(TranslationsKeysCommon.NO),
                            value: 'false',
                        },
                    ],
                    label: field.config.label,
                    orientation: 'tray',
                };
            case 'enable_saml':
                return {
                    ...field.config,
                    isTextValueColorLight: !data.enable_saml,
                };
            default:
                return field.config;
        }
    };
    const updateFormFields = (formData = {}, errorMessages = {}) => {
        const fieldsToShow = [];
        if (!data.enable_saml && mode === Mode.VIEW) {
            const configuredStatus = formConfig.find((config) => config.key === 'enable_saml');
            fieldsToShow.push({
                ...configuredStatus,
                configCallback: getFieldConfig,
            });
        }
        else {
            formConfig.forEach((config) => {
                if (mode === Mode.VIEW && config.view) {
                    if (config.key === 'automatically_create_users') {
                        const updatedConfig = { ...config };
                        updatedConfig.config = {
                            ...updatedConfig.config,
                            label: getTranslations(TranslationsKeys.AUTOMATICALLY_CREATE_USERS),
                        };
                        fieldsToShow.push({
                            ...updatedConfig,
                            configCallback: getFieldConfig,
                        });
                    }
                    else {
                        fieldsToShow.push({
                            ...config,
                            configCallback: getFieldConfig,
                        });
                    }
                }
                else if (mode === Mode.EDIT && config.elementType) {
                    if (config.key === 'idp_metadata' &&
                        formData?.idp_metadata === METADATA_FILE_NAME) {
                        const updatedConfig = { ...config };
                        delete updatedConfig.validation.required;
                        fieldsToShow.push({
                            ...updatedConfig,
                            configCallback: getFieldConfig,
                        });
                    }
                    else {
                        fieldsToShow.push({
                            ...config,
                            configCallback: getFieldConfig,
                        });
                    }
                }
            });
        }
        const newFormFields = getFormFields(formData, fieldsToShow, errorMessages, mode);
        setFormFields(newFormFields);
    };
    useEffect(() => {
        showLoader();
    }, []);
    useEffect(() => {
        if (samlData && !isLoading) {
            fetchSaml();
        }
    }, [samlData, isLoading]);
    useEffect(() => {
        if (!_.isEmpty(data)) {
            updateFormFields(data);
        }
    }, [data, mode]);
    const validateForm = (currentFormData = {}, editedField = '') => {
        let anyInValid = false;
        if (editedField) {
            const editedData = currentFormData.filter((i) => i.id === editedField)[0] ||
                {};
            if (editedData?.valid === false) {
                anyInValid = true;
            }
        }
        else {
            currentFormData.forEach((i) => {
                if (i &&
                    ((i.id === 'idp_metadata' &&
                        i.value === METADATA_FILE_NAME &&
                        i.file &&
                        !i.valid) ||
                        i.valid === false)) {
                    anyInValid = true;
                }
            });
        }
        return anyInValid;
    };
    const onSave = useCallback((currentFormData) => {
        const isFormInValid = validateForm(currentFormData);
        if (!isFormInValid) {
            const formData = new FormData();
            currentFormData.forEach((formInput) => {
                switch (formInput.id) {
                    case SAML_FORM_FIELDS.SERVER_NAME:
                        formData.append(SAML_FORM_FIELDS.SERVER_NAME, formInput.value || '');
                        break;
                    case SAML_FORM_FIELDS.PROTOCOL:
                        formData.append(SAML_FORM_FIELDS.PROTOCOL, formInput.value || '');
                        break;
                    case SAML_FORM_FIELDS.SERVER_PORT:
                        formData.append(SAML_FORM_FIELDS.SERVER_PORT, formInput.value || '');
                        break;
                    case SAML_FORM_FIELDS.SERVICE_NAME:
                        formData.append(SAML_FORM_FIELDS.SERVICE_NAME, formInput.value || '');
                        break;
                    case SAML_FORM_FIELDS.RESPONSE_SKEW:
                        formData.append(SAML_FORM_FIELDS.RESPONSE_SKEW, formInput.value || SAML_DEFAULT_RESPONSE_SKEW);
                        break;
                    case SAML_FORM_FIELDS.AUTOMATICALLY_CREATE_USERS:
                        formData.append(SAML_FORM_FIELDS.AUTOMATICALLY_CREATE_USERS, String(formInput.value === 'true' ||
                            formInput.value === true));
                        break;
                    case SAML_FORM_FIELDS.IDP_METADATA:
                        if (formInput.file) {
                            formData.append(SAML_FORM_FIELDS.IDP_METADATA, formInput.file);
                        }
                        else if (formInput.value) {
                            formData.append(SAML_FORM_FIELDS.IDP_METADATA, formInput.value);
                        }
                        break;
                    default:
                        break;
                }
            });
            postSamlData(formData);
        }
    }, []);
    const actionHandler = (type, currentFormData = {}) => {
        switch (type) {
            case ActionButtons.EDIT:
                setMode(Mode.EDIT);
                break;
            case ActionButtons.BACK:
            case ActionButtons.CANCEL:
                setMode(Mode.VIEW);
                break;
            case ActionButtons.SAVE:
                onSave(currentFormData);
                break;
            case ActionButtons.DELETE:
                setDeleteConfirm(true);
                break;
            default:
                setMode(Mode.EDIT);
                break;
        }
    };
    const handleIsEdited = (currentFormData, id) => {
        validateForm(currentFormData, id);
    };
    const renderContent = () => {
        const config = {
            title: getTranslations(TranslationsKeys.SAML_CONFIGURED_HEADING),
            formFields,
        };
        const actions = {
            primary: {
                type: ActionButtons.SAVE,
                text: getTranslations(TranslationsKeys.SAVE),
                returnData: true,
            },
            secondary: {
                type: ActionButtons.CANCEL,
                text: getTranslations(TranslationsKeys.CANCEL),
            },
        };
        return (React.createElement("div", { className: cx(styles.samlContentWrapper, {
                [styles.samlContentWrapperMigration]: isMigrationBannerVisible,
            }) },
            React.createElement(Content, { key: `${pageName}_${mode}`, config: config, mode: mode, backConfirm: true, actions: actions, pageName: pageName, showHeader: false, actionHandler: actionHandler, onEdit: handleIsEdited }),
            mode === Mode.VIEW && data.enable_saml && (React.createElement(Vertical, { spacing: "e" },
                React.createElement(Horizontal, { className: styles.formItemContainer },
                    React.createElement(Typography, { variant: "p", className: cx(styles.forRowWidthViewMode, {
                            [styles.marginBottom]: isMigrationBannerVisible,
                        }) }, getTranslations(TranslationsKeysCommon.SAML_IDP_METADATA)),
                    React.createElement(Link, { className: styles.forRowWidthViewValue, onClick: () => onDownloadCertificateUtil(METADATA_PATH, 'Spring_saml_metadata.xml') },
                        React.createElement(Typography, { variant: "p", color: Colors.accent, className: cx({
                                [styles.marginBottom]: isMigrationBannerVisible,
                            }) }, getTranslations(TranslationsKeysCommon.DOWNLOAD_BUTTON_TEXT)))),
                React.createElement(Horizontal, { className: styles.formItemContainer },
                    React.createElement(Typography, { variant: "p", className: cx(styles.forRowWidthViewMode, {
                            [styles.marginBottom]: isMigrationBannerVisible,
                        }) }, getTranslations(TranslationsKeysCommon.SAML_TS_METADATA)),
                    React.createElement(Link, { className: styles.forRowWidthViewValue, onClick: () => onDownloadCertificateUtil(CALLOSUM_CERTIFICATE_PATH, 'TS_SAML_X509_certificate.cer'), tabIndex: 0 },
                        React.createElement(Typography, { variant: "p", color: Colors.accent, className: cx({
                                [styles.marginBottom]: isMigrationBannerVisible,
                            }) }, getTranslations(TranslationsKeysCommon.DOWNLOAD_BUTTON_TEXT))))))));
    };
    const onConfirm = useCallback(() => {
        showLoader();
        deleteSaml();
        setDeleteConfirm(false);
    }, [deleteConfirm]);
    const onDismiss = useCallback(() => setDeleteConfirm(false), [
        deleteConfirm,
    ]);
    const headerActions = [
        {
            type: ActionButtons.EDIT,
            title: data.enable_saml
                ? getTranslations(TranslationsKeys.SAML_MODAL_EDIT)
                : getTranslations(TranslationsKeys.SAML_CONFIGURE_LABEL),
            isDisabled: isMigrationBannerVisible,
        },
        ...(mode === Mode.VIEW && data?.enable_saml
            ? [
                {
                    type: ActionButtons.DELETE,
                    title: getTranslations(TranslationsKeys.SAML_REMOVE_BUTTON_TEXT),
                    isDisabled: isMigrationBannerVisible,
                },
            ]
            : []),
    ];
    const getHeaderTitle = useCallback(() => {
        const title = mode === Mode.EDIT
            ? `${getTranslations(TranslationsKeys.SAML_MODAL_EDIT)} `
            : '';
        return `${title}${getTranslations(TranslationsKeys.SAML_HEADING)}`;
    }, [mode]);
    return error ? (React.createElement(GenericError, null)) : (React.createElement(LoadingIndicator.Global, { hideLoader: !screenLoader, loadingText: shouldDisplayLoaderText && screenLoader
            ? t(TranslationsKeys.SAVE_CONFIRMATION_LOADING_MESSAGE)
            : undefined, className: `${styles.loadIndicator} ${screenLoader ? styles.disallowScroll : ''}` },
        deleteConfirm && (React.createElement(CreateModal, { pageName: pageName, action: "delete", onConfirm: onConfirm, onDismiss: onDismiss })),
        React.createElement("div", { className: styles.samlContainer },
            !isLoading && (React.createElement(Horizontal, { spacing: "d" },
                React.createElement(Vertical, { className: styles.headerContainer },
                    React.createElement(Header, { title: getHeaderTitle(), subTitle: getTranslations(TranslationsKeys.SAML_CONTENT), titleSize: "large", learnMoreKey: LearnMore.SAML, mode: mode, actions: mode === Mode.VIEW
                            ? headerActions
                            : undefined, actionHandler: actionHandler })))),
            isMigrationBannerVisible && (React.createElement(IamV2MigrationAutomationBanner, { setScreenLoader: setScreenLoader, setShouldDisplayLoaderText: setShouldDisplayLoaderText })),
            !isLoading && renderContent())));
};
export const Saml = () => (React.createElement(WithQueryHandler, null,
    React.createElement(SamlContainer, null)));
//# sourceMappingURL=saml.container.js.map